import { LitElement, html } from 'lit'
import { ModalBaseMixin } from '../core/mixins/modal-base.mixin.js'
import { modalStackRegistry } from '../core/registries/modal-stack-registry.js'

/**
 * Modal de confirmação para ações destrutivas ou importantes
 * Suporta tipos warning e danger com ícones correspondentes
 * Utiliza tokens CSS do Design System para cores e estilos visuais
 *
 * Aplica ModalBaseMixin para integração com History API (popstate),
 * botão voltar iOS e evento padronizado modal-closed.
 * Nota: Este modal usa `isOpen` como propriedade de abertura.
 * O método close() delega para _handleCancel() para manter
 * compatibilidade com o comportamento existente.
 */
export class ModalConfirm extends ModalBaseMixin(LitElement) {
  static properties = {
    ...super.properties,
    isOpen: { type: Boolean },
    title: { type: String },
    message: { type: String },
    confirmText: { type: String },
    cancelText: { type: String },
    type: { type: String },
    confirmDataCy: { type: String },
    cancelDataCy: { type: String }
  }

  createRenderRoot() {
    return this
  }

  constructor() {
    super()
    this.isOpen = false
    this.title = ''
    this.message = ''
    this.confirmText = 'Confirmar'
    this.cancelText = 'Cancelar'
    this.type = 'warning'
    this.confirmDataCy = 'modal-confirm-ok-button'
    this.cancelDataCy = 'modal-confirm-cancel-button'
  }

  render() {
    if (!this.isOpen) return html``

    const icons = {
      warning: '⚠️',
      danger: '❌'
    }

    return html`
      <div 
        class="modal is-active" 
        @click="${this._handleBackdropClick}"
        data-cy="modal-confirm"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          z-index: 30000;
          display: flex;
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="modal-card" 
          @click="${e => e.stopPropagation()}"
          style="
            max-width: 400px;
            width: 90%;
            min-height: auto;
            background-color: var(--bg-surface, #151C30);
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            animation: modalIn 0.2s ease-out;
          "
        >
          <header class="modal-card-head" style="background-color: var(--bg-surface, #151C30); border-bottom: 1px solid var(--border, #26304A);">
            ${this.renderBotaoVoltarIOS()}
            <p class="modal-card-title" style="color: var(--text-primary, #F4F7FF);" data-cy="modal-confirm-title">${this.title || 'Confirmação'}</p>
          </header>
          <section class="modal-card-body has-text-centered" style="padding: 1.5rem; color: var(--text-primary, #F4F7FF); background-color: var(--bg-surface, #151C30);">
            <div style="font-size: 1.5rem; margin-bottom: 0.75rem;">${icons[this.type] || icons.warning}</div>
            <p style="margin-bottom: 0; color: var(--text-primary, #F4F7FF);">${this.message}</p>
          </section>
          <footer class="modal-card-foot is-justify-content-space-between" style="background-color: var(--bg-surface, #151C30); border-top: 1px solid var(--border, #26304A);">
            <button 
              class="button" 
              @click="${this._handleCancel}"
              data-cy="${this.cancelDataCy}"
            >
              ${this.cancelText}
            </button>
            <button 
              class="button is-danger" 
              @click="${this._handleConfirm}"
              data-cy="${this.confirmDataCy}"
              style="background-color: var(--danger, #EF4444);"
            >
              ${this.confirmText}
            </button>
          </footer>
        </div>
      </div>
      
      <style>
        @keyframes modalIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      </style>
    `
  }

  _handleBackdropClick() {
    this._handleCancel()
  }

  /**
   * Fecha o modal via cancelamento.
   *
   * Seta isOpen=false, sincroniza open do mixin, dispara modal-cancelled
   * e modal-closed. Não chama super.close() diretamente para evitar
   * duplicação do evento modal-closed.
   * Se o fechamento não veio do popstate e havia histórico pendente,
   * executa limpeza de histórico órfão (Req 5.2).
   *
   * @param {object} [options={}] - Opções de fechamento
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  _handleCancel({ fromPopstate = false } = {}) {
    const hadHistoryState = this._historyStatePushed

    this.isOpen = false
    this.open = false
    this._historyStatePushed = false
    this.dispatchEvent(new CustomEvent('modal-cancelled', {
      bubbles: true,
      composed: true
    }))
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }))

    modalStackRegistry.unregister(this)

    // Limpeza de histórico órfão para fechamento por trigger direto
    if (!fromPopstate && hadHistoryState) {
      this._cleanupOrphanHistory()
    }
  }

  /**
   * Confirma a ação e fecha o modal.
   *
   * Seta isOpen=false, sincroniza open do mixin, dispara modal-confirmed
   * e modal-closed. Não chama super.close() diretamente para evitar
   * duplicação do evento modal-closed.
   * Se o fechamento não veio do popstate e havia histórico pendente,
   * executa limpeza de histórico órfão (Req 5.2).
   */
  _handleConfirm() {
    const hadHistoryState = this._historyStatePushed

    this.isOpen = false
    this.open = false
    this._historyStatePushed = false
    this.dispatchEvent(new CustomEvent('modal-confirmed', {
      bubbles: true,
      composed: true
    }))
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }))

    modalStackRegistry.unregister(this)

    // Limpeza de histórico órfão para fechamento por trigger direto (confirm é sempre direto)
    if (hadHistoryState) {
      this._cleanupOrphanHistory()
    }
  }

  /**
   * Método close() para integração com o botão voltar iOS do mixin.
   *
   * Delega para _handleCancel() que já gerencia o fechamento completo
   * (isOpen=false, open=false, eventos modal-cancelled e modal-closed).
   * Isso garante que o botão voltar iOS tenha o mesmo comportamento
   * que clicar em "Cancelar".
   *
   * @param {object} [options={}] - Opções de fechamento
   * @param {boolean} [options.fromPopstate=false] - Indica se veio do popstate
   */
  close (options = {}) {
    this._handleCancel(options)
  }

  /**
   * Abre o modal de confirmação com mensagem e opções.
   * Sincroniza `open` do mixin com `isOpen` para integração com History API.
   * @param {string} message - Mensagem de confirmação
   * @param {string} title - Título do modal (padrão: '')
   * @param {string} confirmText - Texto do botão confirmar (padrão: 'Confirmar')
   * @param {string} cancelText - Texto do botão cancelar (padrão: 'Cancelar')
   * @param {string} type - Tipo visual: warning ou danger (padrão: 'warning')
   */
  show(message, title = '', confirmText = 'Confirmar', cancelText = 'Cancelar', type = 'warning') {
    this.message = message
    this.title = title
    this.confirmText = confirmText
    this.cancelText = cancelText
    this.type = type
    this.isOpen = true
    this.open = true
  }
}

customElements.define('modal-confirm', ModalConfirm)
